import React from 'react';
import ErrorMessageComponent from './ErrorMessageComponent';
import { isChunkLoadError } from 'stash/utils/errors';
import { reloadWindowLocation } from 'stash/utils/windowNavigation';

// Guard key on sessionStorage so we don't loop reload if a chunk genuinely
// no longer exists on the server (e.g. a very old tab against a new deploy
// where multiple chunk hashes have rolled). One reload attempt per session.
const CHUNK_RELOAD_GUARD_KEY = 'stash_web_chunk_reload_attempted';

// Module-scoped in-memory fallback. Used when sessionStorage is unavailable
// (Safari private mode, storage disabled, etc.) so that within a single page
// session repeated error-boundary triggers don't all try to reload. It does
// NOT survive a page reload — for that, we rely on a read-back check below.
const memoryStore = new Map<string, string>();

const safeSessionStorage = {
	getItem(key: string) {
		try {
			const value = window.sessionStorage.getItem(key);
			if (value !== null) return value;
		} catch (_e) {
			// fall through to memory
		}
		return memoryStore.has(key) ? memoryStore.get(key) ?? null : null;
	},
	setItem(key: string, value: string) {
		memoryStore.set(key, value);
		try {
			window.sessionStorage.setItem(key, value);
		} catch (_e) {
			// no-op (Safari private mode, quota, etc.). Memory fallback above
			// keeps the guard intact for the remainder of this page session.
		}
	},
};

// Read only the persistent (sessionStorage) layer, bypassing the memory
// fallback. Used to verify that the guard will actually survive a reload
// before we trigger one — otherwise we'd loop forever.
const readPersistedGuard = (key: string) => {
	try {
		return window.sessionStorage.getItem(key);
	} catch (_e) {
		return null;
	}
};

// Test-only: the module-scoped memory fallback persists across Jest tests
// in the same worker. Exported so test setup can reset it between cases.
export const __resetChunkReloadGuardForTests = () => {
	memoryStore.clear();
};

type ErrorBoundaryProps = {
	children?: React.ReactNode;
};

type ErrorBoundaryState = {
	hasError: boolean;
	error: Error | null;
	reloading: boolean;
};

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false, error: null, reloading: false };
	}

	static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error) {
		// Users encounter chunk load errors when the app releases new chunks
		// or when WebKit fails the speculative module/CSS preload. Reload to
		// recover, but only once per session to avoid infinite loops if the
		// chunk is genuinely gone.
		if (isChunkLoadError(error)) {
			if (safeSessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) === '1') {
				return;
			}
			safeSessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, '1');
			// Verify the guard actually persisted in sessionStorage. If the
			// write was swallowed (Safari private mode, quota, storage
			// disabled), reloading would loop forever because the guard
			// can't survive the reload. Surface the error UI instead.
			if (readPersistedGuard(CHUNK_RELOAD_GUARD_KEY) !== '1') {
				return;
			}
			this.setState({ reloading: true });
			console.error('Chunk reloaded');
			reloadWindowLocation();
		}
	}

	render() {
		if (this.state.hasError) {
			// Suppress the error UI while a chunk-error reload is in-flight so
			// the user doesn't see a flash of "Whoops" before the page refreshes.
			if (this.state.reloading) {
				return null;
			}
			return <ErrorMessageComponent />;
		}

		return this.props.children;
	}
}

export default ErrorBoundary;
